import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { LatLng } from "@/shared/contracts";
import { schematicProjection } from "@/shared/map/projection";
import { Icon } from "@/shared/ui";

/**
 * 访客星球地图的 2D 示意图层（总览占位 GlobeSlot 与“地区以下”的 2D 顶替都用它）：
 * Web Mercator（shared/map/projection 同一套）把 bounds 装进容器，再叠加用户的平移、缩放；底下只有水色和网格，不冒充真实底图。
 * 拖动（指针按在底图上）、双指缩放、滚轮、加减按钮、键盘（方向键移动、加号减号缩放）。
 * 标记由 children(project) 画：只用 project 把 WGS-84 坐标换成屏幕像素，标记不改坐标。
 * 不用 shared/map 的 SchematicMapSurface：它会向服务注册表要底图服务（访客读不到要登录的底图），也不能从外面接回镜头。
 */
export type MapView = { scale: number; tx: number; ty: number };
export const IDENTITY_VIEW: MapView = { scale: 1, tx: 0, ty: 0 };
export type Project = (at: LatLng) => { x: number; y: number };

/** 浮在地图边上的控件占的地方（px）：装点时让开 */
export const INSETS = { top: 124, right: 64, bottom: 186, left: 24 };
const MIN_SCALE = 0.5;
const MAX_SCALE = 12;

export function Map2D({
  bounds,
  view,
  onView,
  label,
  showZoomControls = true,
  children,
}: {
  bounds: LatLng[];
  view: MapView;
  onView: (next: MapView | ((prev: MapView) => MapView)) => void;
  label: string;
  showZoomControls?: boolean;
  children: (project: Project, size: { width: number; height: number }) => ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 360, height: 480 });
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const apply = (width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    const rect = el.getBoundingClientRect();
    apply(Math.round(rect.width), Math.round(rect.height));
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(([entry]) => apply(Math.round(entry.contentRect.width), Math.round(entry.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 装点的时候让开浮在地图边上的东西：上面的返回与品牌、右上的缩放键、下面的署名与“放大看这一带”（INSETS），再四周留 24px
  const base = useMemo(() => {
    const inner = schematicProjection(bounds.length ? bounds : [{ lat: 22.3, lng: 114.17 }], { width: Math.max(1, size.width - INSETS.left - INSETS.right), height: Math.max(1, size.height - INSETS.top - INSETS.bottom) }, 24);
    return (at: LatLng) => {
      const p = inner(at);
      return { x: p.x + INSETS.left, y: p.y + INSETS.top };
    };
  }, [bounds, size]);
  const project = useCallback<Project>(
    (at) => {
      const b = base(at);
      return { x: b.x * view.scale + view.tx, y: b.y * view.scale + view.ty };
    },
    [base, view],
  );

  const zoomBy = useCallback(
    (factor: number, cx = size.width / 2, cy = size.height / 2) => {
      onView((v) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
        const real = scale / v.scale;
        return { scale, tx: cx - (cx - v.tx) * real, ty: cy - (cy - v.ty) * real };
      });
    },
    [size, onView],
  );

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-map-interactive]")) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomBy(event.deltaY < 0 ? 1.15 : 1 / 1.15, event.clientX - rect.left, event.clientY - rect.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest("[data-map-interactive]")) return;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* 合成事件、旧浏览器不支持捕获：拖动照样可用 */
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: view.scale };
    }
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(event.pointerId);
    if (!prev) return;
    const next = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, next);
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const rect = event.currentTarget.getBoundingClientRect();
      const target = pinch.current.scale * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.dist);
      zoomBy(target / view.scale, (a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top);
      return;
    }
    onView((v) => ({ ...v, tx: v.tx + next.x - prev.x, ty: v.ty + next.y - prev.y }));
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const step = 60;
    const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      onView((v) => ({ ...v, tx: v.tx + move[0], ty: v.ty + move[1] }));
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomBy(1.3);
    } else if (event.key === "-") {
      event.preventDefault();
      zoomBy(1 / 1.3);
    }
  };

  const step = 48 * view.scale;
  return (
    <>
      <div
        ref={hostRef}
        className="ps-pmap__surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="group"
        aria-label={label}
        data-basemap="schematic"
      >
        <svg className="ps-pmap__base" width={size.width} height={size.height} aria-hidden="true" focusable="false">
          <defs>
            <pattern id="ps-pmap-grid" width={step} height={step} patternUnits="userSpaceOnUse" x={view.tx % step} y={view.ty % step}>
              <path d={`M ${step} 0 L 0 0 0 ${step}`} fill="none" stroke="var(--c-map-grid)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" className="ps-pmap__water" />
          <rect width="100%" height="100%" fill="url(#ps-pmap-grid)" />
        </svg>
        {children(project, size)}
      </div>
      {showZoomControls ? <div className="ps-pmap__zoom" data-map-interactive>
        <button type="button" className="ps-pmap__ctl" aria-label="放大地图" onClick={() => zoomBy(1.4)}>
          <Icon name="plus" size={18} />
        </button>
        <button type="button" className="ps-pmap__ctl" aria-label="缩小地图" onClick={() => zoomBy(1 / 1.4)}>
          <Icon name="minus" size={18} />
        </button>
      </div> : null}
    </>
  );
}
